import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
const FlowPage = lazy(() => import('../flow/flow-page'));
import { useAuth } from '../lib/auth';
import { ConsoleShell, landingFor } from '../layouts/ConsoleShell';
import { Login } from '../routes/Login';
import { SetPassword } from '../routes/SetPassword';
import { ForgotPassword } from '../routes/ForgotPassword';
import { NoConsole } from '../routes/NoConsole';
import { Leads } from '../consoles/ops/routes/Leads';
import { LeadDetail } from '../consoles/ops/routes/LeadDetail';
import { Cases } from '../consoles/ops/routes/Cases';
import { CaseDetail } from '../consoles/ops/routes/CaseDetail';
import { Staff as PartnerStaff } from '../consoles/partner/routes/Staff';
import { Camps as PartnerCamps } from '../consoles/partner/routes/Camps';
import { Profile as PartnerProfile } from '../consoles/partner/routes/Profile';
import { Earnings as PartnerEarnings } from '../consoles/partner/routes/Earnings';
import { Security } from '../routes/Security';
import { MySignature } from '../routes/MySignature';
import { Dashboard } from '../consoles/hr/routes/Dashboard';
import { Employees } from '../consoles/hr/routes/Employees';
import { EmployeeImport, SupportEmployeeImport } from '../consoles/hr/routes/EmployeeImport';
import { EmployeeNew } from '../consoles/hr/routes/EmployeeNew';
import { EmployeePrintQr } from '../consoles/hr/routes/EmployeePrintQr';
import { EmployeeDetail } from '../consoles/hr/routes/EmployeeDetail';
import { Appointments } from '../consoles/hr/routes/Appointments';
import { Certificates } from '../consoles/hr/routes/Certificates';
import { DueList } from '../consoles/hr/routes/DueList';
import { Reports } from '../consoles/hr/routes/Reports';
import { Settings } from '../consoles/hr/routes/Settings';
import { NotificationsPage } from '../components/NotificationsPage';
import { Examinations } from '../consoles/hr/routes/Examinations';
import { Compliance } from '../consoles/hr/routes/Compliance';
import { Campaigns } from '../consoles/hr/routes/Campaigns';
import { Users } from '../consoles/hr/routes/Users';
import { Moved } from '../consoles/hr/routes/Moved';
import { ExaminationDetail } from '../consoles/hr/routes/ExaminationDetail';
import { AppointmentDetail } from '../consoles/hr/routes/AppointmentDetail';
import { CertificateDetail } from '../consoles/hr/routes/CertificateDetail';
import { Treatments } from '../consoles/hr/routes/Treatments';
import { EmergencyBills } from '../consoles/hr/routes/EmergencyBills';
import { EmergencyVisits, EmergencyVisitDetail } from '../consoles/partner/routes/Emergency';
import { CampaignDetailPage } from '../consoles/hr/routes/Campaigns';
import { InvoiceDetailPage } from '../components/InvoiceDetailPage';
import { Companies } from '../consoles/admin/routes/Companies';
import { Partners } from '../consoles/admin/routes/Partners';
import { PartnerDetail } from '../consoles/admin/routes/PartnerDetail';
import { CompanyDetail } from '../consoles/admin/routes/CompanyDetail';
import { Physicians } from '../consoles/admin/routes/Physicians';
import { ReviewCorrections } from '../consoles/admin/routes/ReviewCorrections';
import { Roles } from '../consoles/admin/routes/Roles';
import { Users as PlatformUsers } from '../consoles/admin/routes/Users';
import { Permissions } from '../consoles/admin/routes/Permissions';
import { Masters } from '../consoles/admin/routes/Masters';
import { Overview } from '../consoles/admin/routes/Overview';
import { Oversight } from '../consoles/admin/routes/Oversight';
import { Impersonation } from '../consoles/admin/routes/Impersonation';
import { EmailSettings, Settings as AdminSettings } from '../consoles/admin/routes/Settings';
import { IndustryDetail } from '../consoles/admin/routes/IndustryDetail';
import { UserDetail } from '../consoles/admin/routes/UserDetail';
import { PhysicianDetail } from '../consoles/admin/routes/PhysicianDetail';
import { TestDetail } from '../consoles/admin/routes/TestDetail';
import { HazardDetail } from '../consoles/admin/routes/HazardDetail';
import { ProtocolDetail } from '../consoles/admin/routes/ProtocolDetail';
import { PackageDetail } from '../consoles/admin/routes/PackageDetail';
import { Billing as AdminBilling } from '../consoles/admin/routes/Billing';
import { PaymentReview } from '../consoles/admin/routes/PaymentReview';
import { EmergencyAdminVisit, EmergencyQueue } from '../consoles/admin/routes/EmergencyConfirm';
import { Billing as HrBilling } from '../consoles/hr/routes/Billing';
import { Queue as HospitalQueue } from '../consoles/hospital/routes/Queue';
import { CheckIn } from '../consoles/hospital/routes/CheckIn';
import { Encounter } from '../consoles/hospital/routes/Encounter';
import { Worklist } from '../consoles/lab/routes/Worklist';
import { Results } from '../consoles/lab/routes/Results';
import { Release } from '../consoles/lab/routes/Release';
import { Reports as LabReports } from '../consoles/lab/routes/Reports';
import { Verifications as LabVerifications } from '../consoles/lab/routes/Verifications';
import { Queue as PhysicianQueue } from '../consoles/physician/routes/Queue';
import { Review } from '../consoles/physician/routes/Review';
import { History as PhysicianHistory } from '../consoles/physician/routes/History';
import { MyEarnings } from '../consoles/staff/routes/MyEarnings';

/**
 * Requires a session.
 *
 * `bootstrapping` matters: the access token lives in memory and is gone after
 * a reload, so the app spends a moment re-establishing it from the refresh
 * cookie. Rendering the login screen during that window would bounce a
 * signed-in user out of the page they just refreshed.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { principal, bootstrapping } = useAuth();
  const loc = useLocation();

  if (bootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50" role="status">
        <div aria-hidden className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600 motion-reduce:animate-none" />
        <span className="sr-only">Signing you back in…</span>
      </div>
    );
  }

  if (!principal) {
    // Remember where they were headed, so signing in returns them there.
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * Where "/" goes.
 *
 * Not a fixed destination: a hospital coordinator sent to the HR dashboard
 * meets a 403 as their first impression of the product, which teaches people
 * that errors are normal — the same reason the navigation is filtered at all.
 */
function Home() {
  const { permissions, principal } = useAuth();
  return <Navigate to={landingFor(permissions, principal)} replace />;
}

/**
 * Where "/ops" goes: the case desk, as the menu and the sign-in landing put
 * it first (flow audit 2026-10-20, 15.2) — or, for somebody without it, the
 * first desk they do hold.
 */
function OpsHome() {
  const { permissions } = useAuth();
  const has = (p: string) => permissions.has(p);
  const to = has('case.read-ops') ? '/ops/cases' : has('lead.read') ? '/ops/leads' : '/ops/notifications';
  return <Navigate to={to} replace />;
}

/**
 * A mistyped or out-of-date link.
 *
 * Says what happened and offers the one way out. A bare sentence on a blank
 * page left the reader with the browser's back button as the only exit, and
 * a link from an old email has no "back".
 */
function NotFound() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-sm rounded-card border border-neutral-200 bg-white p-6 text-center shadow-card">
        <p className="text-caption font-semibold uppercase tracking-wider text-neutral-500">Page not found</p>
        <h1 className="mt-1 text-h3 text-neutral-900">That page does not exist</h1>
        <p className="mt-2 text-small text-neutral-600">
          The link may be mistyped, or the page may have moved. Nothing has changed in your account.
        </p>
        <Link
          to="/"
          className="mt-5 inline-flex h-10 items-center justify-center rounded-btn bg-primary-600 px-4 text-body font-semibold text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Go to my home page
        </Link>
      </div>
    </main>
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/flow" element={<RequireAuth><Suspense fallback={<div className="flow-fallback" role="status">Loading business flow…</div>}><FlowPage /></Suspense></RequireAuth>} />
      {/* The three pages an email link lands on. Public: whoever holds the
          link has no session yet, and that is the point of the link. */}
      <Route path="/invitation/:token" element={<SetPassword purpose="invitation" />} />
      <Route path="/reset-password/:token" element={<SetPassword purpose="reset" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* The ID slip prints on its own, without the console around it. */}
      <Route path="/hr/employees/:id/print-qr" element={<RequireAuth><EmployeePrintQr /></RequireAuth>} />

      <Route path="/hr" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        {/* The console's own landing, not a fixed page: a billing-only login
            has no dashboard to read. */}
        <Route index element={<Home />} />
        <Route path="dashboard" element={<Dashboard />} />
        {/* Retired setup screens (docs/38): each address says where things went. */}
        <Route path="onboarding" element={<Moved what="setup" />} />
        <Route path="setup" element={<Moved what="setup" />} />
        <Route path="job-roles" element={<Moved what="job-roles" />} />
        <Route path="medical-config" element={<Moved what="medical-config" />} />
        <Route path="employees" element={<Employees />} />
        {/* Both static segments come BEFORE :id, or "new" and "import" are
            read as employee ids and 404 against the API. */}
        <Route path="employees/new" element={<EmployeeNew />} />
        <Route path="employees/import" element={<EmployeeImport />} />
        <Route path="employees/:id" element={<EmployeeDetail />} />
        <Route path="examinations" element={<Examinations />} />
        <Route path="examinations/:id" element={<ExaminationDetail />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="appointments/:id" element={<AppointmentDetail />} />
        <Route path="certificates" element={<Certificates />} />
        <Route path="certificates/:id" element={<CertificateDetail />} />
        <Route path="treatments" element={<Treatments />} />
        <Route path="emergency-bills" element={<EmergencyBills />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="compliance/due" element={<DueList />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
        <Route path="users" element={<Users />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="billing" element={<HrBilling tab="overview" />} />
        {/* Static before :id, or "invoices" is read as an invoice id. */}
        <Route path="billing/invoices" element={<HrBilling tab="invoices" />} />
        <Route path="billing/:id" element={<InvoiceDetailPage console="hr" />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
      </Route>

      <Route path="/admin" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        <Route index element={<Navigate to="/admin/overview" replace />} />
        <Route path="overview" element={<Overview />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="companies/:id/employees/import" element={<SupportEmployeeImport />} />
        <Route path="companies/:id/oversight" element={<Oversight />} />
        <Route path="impersonation" element={<Impersonation />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="payments" element={<PaymentReview />} />
        <Route path="emergency" element={<EmergencyQueue />} />
        <Route path="emergency/:tenantId/:id" element={<EmergencyAdminVisit />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage console="admin" />} />
        <Route path="partners" element={<Partners />} />
        <Route path="partners/register" element={<Navigate to="/admin/partners?register=1" replace />} />
        <Route path="partners/:id" element={<PartnerDetail />} />
        <Route path="physicians" element={<Physicians />} />
        <Route path="physicians/:id" element={<PhysicianDetail />} />
        <Route path="review-corrections" element={<ReviewCorrections />} />
        <Route path="masters" element={<Masters />} />
        <Route path="suggestions" element={<Navigate to="/admin/masters?tab=suggestions" replace />} />
        <Route path="masters/industries/:id" element={<IndustryDetail />} />
        <Route path="masters/tests/:id" element={<TestDetail />} />
        <Route path="masters/hazards/:id" element={<HazardDetail />} />
        <Route path="masters/protocols/:id" element={<ProtocolDetail />} />
        <Route path="masters/packages/:id" element={<PackageDetail />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="email" element={<EmailSettings />} />
        <Route path="users" element={<PlatformUsers />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="roles" element={<Roles />} />
        <Route path="permissions" element={<Permissions />} />
        {/* The audit log lives in Settings now (29 Oct 2026); old links land there. */}
        <Route path="audit" element={<Navigate to="/admin/settings?tab=audit" replace />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      {/* One venue, one console.
          The clinical screens are the very components the two older consoles
          use — a queue is a queue whoever opens it — so this tree is routing
          and not a second implementation. What is new is that one person sees
          the whole of their venue's day instead of half of it in each of two
          places. */}
      {/* Operations: the lead inbox, and Pravinex's own. Outside every tenant
          scope — no customer and no venue reaches any of it. */}
      <Route path="/ops" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        <Route index element={<OpsHome />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="cases" element={<Cases />} />
        <Route path="cases/:tenantId/:id" element={<CaseDetail />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/partner" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        {/* Not a fixed screen: the queue needs `encounter.read`, which the
            bench and the pathologist do not hold, so the console's own home
            link gave them a permission error. */}
        <Route index element={<Home />} />
        <Route path="queue" element={<HospitalQueue />} />
        <Route path="check-in" element={<CheckIn />} />
        <Route path="encounter/:id" element={<Encounter />} />
        <Route path="samples" element={<Worklist />} />
        <Route path="results" element={<Results />} />
        <Route path="release" element={<Release />} />
        <Route path="reports" element={<LabReports />} />
        <Route path="staff" element={<PartnerStaff />} />
        <Route path="camps" element={<PartnerCamps />} />
        <Route path="emergency" element={<EmergencyVisits />} />
        <Route path="emergency/:id" element={<EmergencyVisitDetail />} />
        <Route path="profile" element={<PartnerProfile />} />
        {/* A venue's own statements, not the clinician ledger `MyEarnings`
            reads: a hospital is paid once for a period across every company
            it served, and a physician is paid for a sign-off they made. */}
        <Route path="earnings" element={<PartnerEarnings />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/hospital" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        <Route index element={<Navigate to="/hospital/queue" replace />} />
        <Route path="queue" element={<HospitalQueue />} />
        <Route path="check-in" element={<CheckIn />} />
        <Route path="encounter/:id" element={<Encounter />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/lab" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        <Route index element={<Navigate to="/lab/worklist" replace />} />
        <Route path="worklist" element={<Worklist />} />
        <Route path="results" element={<Results />} />
        <Route path="release" element={<Release />} />
        <Route path="reports" element={<LabReports />} />
        <Route path="verifications" element={<LabVerifications />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/physician" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
        <Route index element={<Navigate to="/physician/queue" replace />} />
        <Route path="queue" element={<PhysicianQueue />} />
        <Route path="history" element={<PhysicianHistory />} />
        <Route path="review/:id" element={<Review />} />
        {/* Same screen in both consoles: a physician reaches it from theirs and
            a pathologist from the lab. One component, because "what have I
            earned" is one question. */}
        <Route path="earnings" element={<MyEarnings />} />
        <Route path="security" element={<Security />} />
        <Route path="signature" element={<MySignature />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
      <Route path="/no-console" element={<RequireAuth><NoConsole /></RequireAuth>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
